import { useEffect, useMemo, useState } from "react";

import * as api from "../api/status";
import { ApiException } from "../api/client";
import { StatusTabs } from "../components/StatusTabs";
import { StatusTopBar } from "../components/StatusTopBar";

type StatusFilter = "all" | "active" | "resolved";

const WINDOW_MONTHS = 3;

/** First day of `month` (UTC). */
function monthStart(month: Date): Date {
  return new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), 1),
  );
}

/** First day of the month `offset` months after `month` (UTC). */
function shiftMonth(month: Date, offset: number): Date {
  return new Date(
    Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + offset, 1),
  );
}

function today(): Date {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}

function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

function fmtDayTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function formatDuration(seconds: number | null): string {
  if (seconds === null) return "ongoing";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM > 0 ? `${h} h ${remM} min` : `${h} h`;
}

const SEVERITY_TONE: Record<
  api.IncidentSeverity,
  { dot: string; label: string; chip: string }
> = {
  degraded: {
    dot: "bg-status-degraded",
    label: "Degraded",
    chip: "bg-status-degraded/10 text-status-degraded",
  },
  partial: {
    dot: "bg-status-partial",
    label: "Partial outage",
    chip: "bg-status-partial/10 text-status-partial",
  },
  major: {
    dot: "bg-status-major",
    label: "Major outage",
    chip: "bg-status-major/10 text-status-major",
  },
};

export function History(): JSX.Element {
  const [services, setServices] = useState<api.ServiceCard[]>([]);
  const [items, setItems] = useState<api.IncidentDTO[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [serviceKey, setServiceKey] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  // `monthsBack` = 0 → window ends on current month; 1 → ends last month; etc.
  const [monthsBack, setMonthsBack] = useState(0);

  useEffect(() => {
    void api.getOverview().then((o) => setServices(o.services));
  }, []);

  // Visible months (oldest → newest) and the corresponding fetch window.
  const visibleMonths = useMemo(() => {
    const last = shiftMonth(monthStart(today()), -monthsBack);
    return Array.from({ length: WINDOW_MONTHS }, (_, i) =>
      shiftMonth(last, i - (WINDOW_MONTHS - 1)),
    );
  }, [monthsBack]);

  const periodLabel = useMemo(() => {
    if (visibleMonths.length === 0) return "";
    const first = visibleMonths[0]!;
    const last = visibleMonths[visibleMonths.length - 1]!;
    return `${fmtMonthYear(first)} → ${fmtMonthYear(last)}`;
  }, [visibleMonths]);

  // Fetch incidents whose window overlaps the visible months.
  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    const since = visibleMonths[0]!;
    const until = shiftMonth(visibleMonths[visibleMonths.length - 1]!, 1);
    void api
      .getIncidents({
        status: statusFilter,
        ...(serviceKey ? { service: serviceKey } : {}),
        since: since.toISOString(),
        until: until.toISOString(),
        limit: 500,
      })
      .then((r) => {
        if (!cancelled) setItems(r.items.filter((i: any) => !i.service.includes("mp_") && !i.service.includes("marketplace")));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err instanceof ApiException
              ? err.apiError.message
              : "Failed to load",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [serviceKey, statusFilter, visibleMonths]);

  // Bucket incidents by their startedAt month (YYYY-MM, UTC).
  const itemsByMonth = useMemo(() => {
    const m = new Map<string, api.IncidentDTO[]>();
    if (!items) return m;
    for (const i of items) {
      const d = new Date(i.startedAt);
      const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
      const arr = m.get(key) ?? [];
      arr.push(i);
      m.set(key, arr);
    }
    // Within each month, newest first.
    for (const arr of m.values()) {
      arr.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    }
    return m;
  }, [items]);

  const serviceLabel = (key: string): string =>
    services.find((s) => s.key === key)?.displayName ?? key;

  return (
    <main className="mx-auto max-w-4xl px-6 py-10">
      <StatusTopBar />
      <StatusTabs />

      {/* Filter row: service + status + period stepper. */}
      <section className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2">
          <select
            value={serviceKey}
            onChange={(e) => setServiceKey(e.target.value)}
            className="rounded-md border border-white/10 bg-dr-surface/60 px-3 py-2 text-sm text-dr-txt"
          >
            <option value="">All services</option>
            {services.map((s) => (
              <option key={s.key} value={s.key}>
                {s.displayName}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded-md border border-white/10 bg-dr-surface/60 px-3 py-2 text-sm text-dr-txt"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-text-secondary">{periodLabel}</span>
          <div className="flex overflow-hidden rounded-md border border-white/10">
            <button
              onClick={() => setMonthsBack((m) => m + 1)}
              aria-label="Previous period"
              className="px-3 py-1.5 text-sm text-text-secondary hover:bg-white/5"
            >
              ←
            </button>
            <div className="w-px bg-white/10" />
            <button
              onClick={() => setMonthsBack((m) => Math.max(0, m - 1))}
              disabled={monthsBack === 0}
              aria-label="Next period"
              className="px-3 py-1.5 text-sm text-text-secondary hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-30"
            >
              →
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="card mb-6 text-sm text-status-major">{error}</div>
      )}

      {items === null && !error && (
        <p className="text-sm text-text-muted">Loading…</p>
      )}

      {items && (
        <div className="space-y-10">
          {/* Render newest month first — incidents top-to-bottom chronologically reversed. */}
          {[...visibleMonths].reverse().map((m) => {
            const key = `${m.getUTCFullYear()}-${String(m.getUTCMonth() + 1).padStart(2, "0")}`;
            const monthItems = itemsByMonth.get(key) ?? [];
            return (
              <MonthSection
                key={key}
                month={m}
                items={monthItems}
                serviceLabel={serviceLabel}
              />
            );
          })}
        </div>
      )}
    </main>
  );
}

function MonthSection({
  month,
  items,
  serviceLabel,
}: {
  month: Date;
  items: api.IncidentDTO[];
  serviceLabel: (key: string) => string;
}): JSX.Element {
  return (
    <section>
      <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-text-muted">
        {fmtMonthYear(month)}
      </h2>
      {items.length === 0 ? (
        <div className="rounded-xl border border-white/[0.07] bg-dr-surface/40 px-5 py-4 text-sm text-text-muted">
          No incidents reported.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((i) => (
            <IncidentCard
              key={i.id}
              incident={i}
              serviceName={serviceLabel(i.service)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function IncidentCard({
  incident,
  serviceName,
}: {
  incident: api.IncidentDTO;
  serviceName: string;
}): JSX.Element {
  const tone = SEVERITY_TONE[incident.severity];
  const isActive = incident.endedAt === null;
  return (
    <li className="group rounded-xl border border-white/[0.07] bg-dr-surface/60 p-5 shadow-sm transition-colors hover:border-white/15">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            className={`mt-1.5 inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${tone.dot} ${
              isActive ? "animate-pulse" : ""
            }`}
            aria-hidden
          />
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-dr-heading">
              {incident.summary}
            </h3>
            <p className="mt-1 text-sm text-text-secondary">
              {serviceName}
              {incident.cause && <> · {incident.cause}</>}
            </p>
          </div>
        </div>
        <span
          className={`pill shrink-0 ${tone.chip}`}
          title={isActive ? "Still ongoing" : "Resolved"}
        >
          {isActive ? `${tone.label} · active` : tone.label}
        </span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
        <span>Started {fmtDayTime(incident.startedAt)}</span>
        <span>·</span>
        <span>Duration {formatDuration(incident.durationSeconds)}</span>
        {incident.endedAt && (
          <>
            <span>·</span>
            <span>Resolved {fmtDayTime(incident.endedAt)}</span>
          </>
        )}
      </div>
    </li>
  );
}
